(() => {
  const VIDEO_SELECTOR = 'video.product-media-video';
  const VISIBILITY_THRESHOLD = 0.5;
  const states = new Map();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

  const canAutoplay = () => !reducedMotion.matches && !connection?.saveData;

  const galleryRoot = (video) => video.closest('media-gallery') || video.parentElement;

  const pause = (video) => {
    if (!video.paused) video.pause();
  };

  const play = (video) => {
    if (!video.paused) return;

    const playAttempt = video.play();
    if (playAttempt?.catch) {
      playAttempt.catch(() => {
        // Preserve a usable manual fallback when browser autoplay policy intervenes.
        const state = states.get(video);
        if (state) state.autoplayRejected = true;
        video.controls = true;
      });
    }
  };

  const syncGallery = (root) => {
    if (!root) return;

    const videos = Array.from(root.querySelectorAll(VIDEO_SELECTOR)).filter((video) => states.has(video));
    const autoplayAllowed = canAutoplay() && !document.hidden;

    videos.forEach((video) => {
      video.preload = 'none';
      video.controls = !autoplayAllowed || states.get(video).autoplayRejected;
    });

    if (!autoplayAllowed) {
      videos.forEach(pause);
      return;
    }

    const winner = videos
      .filter(
        (video) =>
          !states.get(video).autoplayRejected && states.get(video).ratio >= VISIBILITY_THRESHOLD
      )
      .sort((a, b) => states.get(b).ratio - states.get(a).ratio)[0];

    videos.forEach((video) => {
      if (video === winner) play(video);
      else pause(video);
    });
  };

  const refresh = () => {
    const roots = new Set();

    states.forEach((state, video) => {
      if (!video.isConnected) {
        state.observer.disconnect();
        states.delete(video);
        return;
      }

      // A display:none gallery has no box; clear stale intersection state immediately
      // while leaving clipped-slide visibility to IntersectionObserver.
      if (!video.getClientRects().length) state.ratio = 0;
      roots.add(galleryRoot(video));
    });

    roots.forEach(syncGallery);
  };

  const init = (video) => {
    if (states.has(video)) return;

    video.preload = 'none';
    const state = { ratio: 0, observer: null, autoplayRejected: false };
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          state.ratio = entry.isIntersecting ? entry.intersectionRatio : 0;
        });
        syncGallery(galleryRoot(video));
      },
      { threshold: [0, VISIBILITY_THRESHOLD, 1] }
    );

    state.observer = observer;
    states.set(video, state);
    observer.observe(video);
  };

  const scan = (root = document) => {
    if (root.matches?.(VIDEO_SELECTOR)) init(root);
    root.querySelectorAll?.(VIDEO_SELECTOR).forEach(init);
    refresh();
  };

  const cleanup = (root) => {
    states.forEach((state, video) => {
      if (video === root || root.contains(video)) {
        pause(video);
        state.observer.disconnect();
        states.delete(video);
      }
    });
  };

  let resizeFrame;
  window.addEventListener(
    'resize',
    () => {
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(refresh);
    },
    { passive: true }
  );
  document.addEventListener('visibilitychange', refresh);
  window.addEventListener('pageshow', refresh);
  document.body.addEventListener('modalClosed', () => window.setTimeout(refresh));
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => cleanup(event.target));
  document.addEventListener('shopify:block:select', refresh);
  document.addEventListener('product-info:loaded', (event) => scan(event.target));

  if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', refresh);
  else reducedMotion.addListener(refresh);
  connection?.addEventListener?.('change', refresh);

  scan();
})();
